import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { useApolloClient } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  VALIDATE_AND_APPLY_COUPON_CODE_TO_CART,
  APPLY_CUSTOMER_GIFTS_TO_CART,
  REMOVE_COUPON_FROM_CART,
} from 'mocks/mutations';

import avo from 'analytics/avo';
import { getValidCouponCode } from '../../../../cart/ApplyCoupon';
import OrderSummaryUI from './OrderSummaryUI';

const getDiscountAmount = (couponCode, couponsList) =>
  couponsList[couponCode].amount_used;

const OrderSummary = ({
  cart,
  orderSummary,
  deliveryInfo,
  shippingGroups,
  shipping,
  selectedTab,
  getCartIssues,
  calculateTotal,
  onSuccessfulCoupon,
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [successCode, setSuccessCode] = useState('');
  const [couponError, setCouponError] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState(false);
  const [couponEditMode, setCouponEditMode] = useState(false);
  const [validateCouponLoading, setValidateCouponLoading] = useState(false);
  const [applyGiftsToCartLoading, setApplyGiftsToCartLoading] = useState(false);
  const [summaryFetched, setSummaryFetched] = useState(false);
  const [promotionName, setPromotionName] = useState(
    orderSummary && orderSummary.promotionName !== ''
      ? orderSummary.promotionName
      : ''
  );
  const [promoCodeSection, setPromoCodeSection] = useState(null);

  const couponCodeRef = useRef();
  const routeMatch = useRouteMatch();
  const client = useApolloClient();

  useEffect(() => {
    if (
      orderSummary &&
      orderSummary.promotionName !== '' &&
      promotionName !== orderSummary.promotionName &&
      promotionName === ''
    ) {
      setPromotionName(orderSummary.promotionName);
    }
  }, [promotionName, orderSummary]);

  const getPageLocation = () => {
    if (routeMatch?.path) {
      if (routeMatch?.path === '/') {
        return avo.Location.HOME_PAGE;
      }
      if (
        [
          '/:slug',
          '/collections/:slug',
          '/collections',
          '/products',
          '/instock',
          '/modular-sofas',
        ].includes(routeMatch?.path)
      ) {
        return avo.Location.CATEGORY_PAGE;
      }
      if (['/:category/:slug', '/modular/:slug'].includes(routeMatch?.path)) {
        return avo.Location.PDP;
      }
      if (routeMatch?.path === '/checkout') {
        return avo.Location.CHECKOUT;
      }
      if (routeMatch?.path === '/free-design-services') {
        return avo.Location.DESIGN_SERVICES;
      }
      return avo.Location.HOME_PAGE; // for other routes we are passing HOME for now;
    }
    return avo.Location.HOME_PAGE;
  };

  const applyCustomerGiftsToCart = async apply => {
    setApplyGiftsToCartLoading(true);
    await client.mutate({
      mutation: APPLY_CUSTOMER_GIFTS_TO_CART,
      variables: { apply },
    });
    await calculateTotal();
    setApplyGiftsToCartLoading(false);
  };

  const submitCouponCode = async () => {
    if (validateCouponLoading) {
      return;
    }

    if (!couponCode) {
      if (couponCodeRef?.current) {
        couponCodeRef.current.focus();
      }
      setCouponError('Please enter coupon code');
    }
    setCouponError(null);
    setValidateCouponLoading(true);

    avo.couponEntered({
      couponName: couponCode,
      location: getPageLocation(),
    });

    const validateResponse = await client.mutate({
      mutation: VALIDATE_AND_APPLY_COUPON_CODE_TO_CART,
      variables: {
        coupon_code: couponCode,
      },
    });
    // Wait for checkoutSummary before success as validate does not check applicability
    const checkoutSummary = await calculateTotal();

    const { success, errors } = validateResponse.data.validateAndApply;
    const {
      applied_coupons,
      promotionName: promotionNameFetched,
    } = checkoutSummary;
    if (!applied_coupons?.length && success) {
      if (couponCodeRef?.current) {
        couponCodeRef.current.focus();
      }
      // Coupon is valid but not applicable
      setCouponError('ValidNA');
      setValidateCouponLoading(false);

      avo.couponDenied({
        couponName: couponCode,
        reason: 'Valid Coupon but not applicable',
        location: getPageLocation(),
      });
    } else if (applied_coupons?.length > 0 && success) {
      const appliedCoupons = applied_coupons.reduce((acc, cur) => {
        acc[cur.coupon_code] = cur;
        return acc;
      }, {});
      const validCode = getValidCouponCode(appliedCoupons, couponCode);
      if (appliedCoupons[validCode]) {
        // Coupon successfully applied - Promotion has been replaced with coupon
        // currently this happens for coupon discounts  >= promotion discount
        // will be changed to > promotion discount soon
        await getCartIssues();

        setCouponEditMode(false);
        setCouponSuccess(true);
        setSuccessCode(
          promotionName !== '' && promotionNameFetched === '' ? 'REPLACED' : ''
        );
        setPromotionName(promotionNameFetched);
        setValidateCouponLoading(false);

        const discountAmt = parseFloat(
          getDiscountAmount(validCode, appliedCoupons)
        );
        avo.couponApplied({
          couponName: validCode,
          discount: discountAmt,
          location: getPageLocation(),
        });

        if (onSuccessfulCoupon) {
          onSuccessfulCoupon(validCode);
        }
      } else {
        if (couponCodeRef?.current) {
          couponCodeRef.current.focus();
        }
        // Coupon valid but not applied
        setCouponError(
          promotionName !== '' && promotionNameFetched === ''
            ? 'ValidNA'
            : 'The coupon code could not be applied.'
        );
        setValidateCouponLoading(false);

        avo.couponDenied({
          couponName: validCode,
          reason: 'Valid Coupon but not applicable',
          location: getPageLocation(),
        });
      }
    } else {
      if (couponCodeRef?.current) {
        couponCodeRef.current.focus();
      }
      const couponErrorValue =
        get(errors, '0.code', '') === 'ERR_CAA'
          ? 'Coupon already applied'
          : 'Invalid coupon code';

      setCouponError(couponErrorValue);
      setValidateCouponLoading(false);

      avo.couponDenied({
        couponName: couponCode,
        reason: couponErrorValue,
        location: getPageLocation(),
      });
    }
  };

  const removeCouponCode = async coupon => {
    const removeResponse = await client.mutate({
      mutation: REMOVE_COUPON_FROM_CART,
      variables: {
        coupon_code: coupon,
      },
    });

    const {
      success,
      message,
    } = removeResponse.data.removeSingleCartAppliedCoupon;

    if (success) {
      await Promise.all([calculateTotal(), getCartIssues()]);
      avo.couponRemoved({
        couponName: couponCode,
        location: getPageLocation(),
      });

      setCouponSuccess(false);
      setPromoCodeSection(true);
      setCouponCode('');
    } else {
      setCouponError(message);
    }
  };

  const toggleCouponEditMode = () => {
    setCouponEditMode(!couponEditMode);
    setCouponCode('');
  };

  const updateCouponCode = e => {
    setCouponCode(e.target.value);
  };

  const clearError = () => {
    setCouponError(null);
    setCouponSuccess(false);
  };

  const summaryIsFetched = () => {
    setSummaryFetched(true);
  };

  // Close coupon success band / Close coupon edit / Clear coupon field
  const toggleCouponSuccess = () => {
    setCouponSuccess(!couponSuccess);
    setCouponEditMode(false);
    setCouponCode('');
    setSuccessCode('');
  };

  return (
    <OrderSummaryUI
      cart={cart}
      couponCode={couponCode}
      couponError={couponError}
      couponSuccess={couponSuccess}
      successCode={successCode}
      couponEditMode={couponEditMode}
      promoCodeSection={promoCodeSection}
      clearError={clearError}
      validateCouponLoading={validateCouponLoading}
      applyGiftsToCartLoading={applyGiftsToCartLoading}
      applyCustomerGiftsToCart={applyCustomerGiftsToCart}
      toggleCouponEditMode={toggleCouponEditMode}
      updateCouponCode={updateCouponCode}
      submitCouponCode={submitCouponCode}
      deliveryInfo={deliveryInfo}
      shippingGroups={shippingGroups}
      shipping={shipping}
      selectedTab={selectedTab}
      orderSummary={orderSummary}
      summaryFetched={summaryFetched}
      summaryIsFetched={summaryIsFetched}
      removeCouponCode={removeCouponCode}
      toggleCouponSuccess={toggleCouponSuccess}
      couponCodeRef={couponCodeRef}
    />
  );
};

OrderSummary.propTypes = {
  calculateTotal: PropTypes.func,
  getCartIssues: PropTypes.func,
  cart: PropTypes.object,
  client: PropTypes.shape({
    mutate: PropTypes.func,
    query: PropTypes.func,
  }),
  deliveryInfo: PropTypes.object,
  onSuccessfulCoupon: PropTypes.func,
  match: PropTypes.shape({
    path: PropTypes.string,
  }),

  orderSummary: PropTypes.shape({
    appliedCoupon: PropTypes.shape({
      code: PropTypes.string,
      discount: PropTypes.shape({
        amount: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
    promotionName: PropTypes.string,
  }),
  shippingGroups: PropTypes.array,
  shipping: PropTypes.object,
  selectedTab: PropTypes.string,
};

export default OrderSummary;
